import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { SupportedFileTypes } from 'src/app/modules/private/file-manager/models';
import { BytesToSizePipe } from '../../pipes/bytes-size.pipe';
import { JoinPipe } from '../../pipes/join.pipe';
import { maxFileSizeInBytes } from 'src/app/app.constants';
import { FileManagerService } from 'src/app/modules/private/file-manager/services/file-manager.service';

@Component({
  selector: 'app-drop-files-area',
  templateUrl: './drop-files-area.component.html',
  styleUrl: './drop-files-area.component.scss',
  standalone: true,
  imports: [CommonModule, NzToolTipModule, BytesToSizePipe, JoinPipe],
})
export class DropFilesAreaComponent implements AfterViewInit {
  readonly message = inject(NzMessageService);
  readonly fileManagerService = inject(FileManagerService);
  @Input('vertical') vertical = false;
  @Input('disabled') disabled = false;
  @Input('limit') limit = Infinity;
  @Output('onUpload') onUpload = new EventEmitter();
  @ViewChild('DropArea', { static: true }) dropArea: ElementRef;
  @ViewChild('FileInput', { static: true }) fileInput: ElementRef;
  readonly SupportedFileTypes = SupportedFileTypes as any;
  readonly maxFileSizeInBytes = maxFileSizeInBytes;

  ngAfterViewInit(): void {
    this.dropArea.nativeElement.addEventListener(
      'dragover',
      this.dragOver.bind(this)
    );
    this.dropArea.nativeElement.addEventListener(
      'click',
      this.openFilePrompt.bind(this)
    );
    this.dropArea.nativeElement.addEventListener(
      'drop',
      this.handleDrop.bind(this)
    );
    this.fileInput.nativeElement.addEventListener(
      'change',
      this.handleFileInput.bind(this)
    );
  }

  @HostListener('document:keydown', ['$event'])
  async copyFromClipboard(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      const supportedType = 'image/png';
      try {
        const permission = await navigator.permissions.query({
          name: 'clipboard-read' as PermissionName,
        });
        if (permission.state === 'denied') {
          this.message.create('error', 'Not allowed to read clipboard.');
        }
        const clipboardContents = await (navigator.clipboard as any).read();
        for (const item of clipboardContents) {
          if (!item.types.includes(supportedType)) {
            this.message.create('error', 'Clipboard contains non-image data.');
          } else {
            const blob = await item.getType(supportedType);
            const file = new File([blob], Date.now() + '.png', {
              type: supportedType,
            });
            this.handleFiles([file]);
          }
        }
      } catch (error) {
        this.message.create('error', error.message);
      }
    }
  }

  dragOver(event) {
    event.preventDefault();
  }

  handleDrop(event) {
    event.preventDefault();
    this.handleFiles(event.dataTransfer.files);
  }

  handleFileInput(event) {
    this.handleFiles(event.target.files);
  }

  openFilePrompt(): void {
    this.fileInput.nativeElement.click();
  }

  handleFiles(files) {
    this.fileManagerService.processDroppedFiles(
      { files, limit: this.limit },
      res => {
        this.onUpload.emit(res);
      }
    );
  }
}
